import React, { useState, useEffect, useMemo } from "react";
import { Spinner, Alert, ButtonGroup, Button } from "react-bootstrap";
import { 
    ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, 
    CartesianGrid, Tooltip, Legend 
} from "recharts";
import { getAnalyticsWidget } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";
import { AnalyticWidgetProps } from "./types";

type SortDirection = "asc" | "desc";
type ExperimentSortField = "experiment_title" | "variant" | "invocation_count" | "play_count" | "conversion_rate";

// Type workarounds for recharts components
const RESP: any = ResponsiveContainer as any;
const CC: any = ComposedChart as any;
const BR: any = Bar as any;
const LN: any = Line as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;

/* eslint-disable camelcase */
type ExperimentDataPoint = {
    experiment_title: string;
    variant: string;
    invocation_count: number;
    play_count: number;
    conversion_rate: number;
};
/* eslint-enable camelcase */

type DisplayMode = "absolute" | "percentage";

/**
 * Transform data for chart display - combines experiment_title and variant
 */
const transformDataForChart = (data: ExperimentDataPoint[]): any[] => {
    return data.map(item => ({
        ...item,
        label: item.variant !== "default" 
            ? `${item.experiment_title} (${item.variant})`
            : item.experiment_title
    }));
};

/**
 * Experiment Comparison Widget - A/B test comparison with conversion rates
 */
const ExperimentComparisonWidget: React.FC<AnalyticWidgetProps> = ({
    customerConfig,
    filters,
    theme,
    chartOptions: _chartOptions
}) => {
    const [analyticsData, setAnalyticsData] = useState<ExperimentDataPoint[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [displayMode, setDisplayMode] = useState<DisplayMode>("absolute");
    const [sortField, setSortField] = useState<ExperimentSortField>("invocation_count");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    const sortedData = useMemo(() => {
        return [...analyticsData].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            if (typeof aVal === "string" && typeof bVal === "string") {
                return sortDirection === "asc" 
                    ? aVal.localeCompare(bVal) 
                    : bVal.localeCompare(aVal);
            }
            return sortDirection === "asc" 
                ? (aVal as number) - (bVal as number) 
                : (bVal as number) - (aVal as number);
        });
    }, [analyticsData, sortField, sortDirection]);

    const handleSort = (field: ExperimentSortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("desc");
        }
    };

    const SortIcon = ({ field }: { field: ExperimentSortField }) => (
        <span style={{ marginLeft: "5px", opacity: sortField === field ? 1 : 0.3 }}>
            {sortField === field && sortDirection === "asc" ? "▲" : "▼"}
        </span>
    );

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true);
            setError(null);
            
            try {
                const response = await getAnalyticsWidget(
                    customerConfig,
                    "experiment_comparison",
                    filters.startDate,
                    filters.endDate,
                    filters.attributionId,
                    filters.brandId,
                    filters.deviceType
                );
                
                const data = (response as any)?.result;
                const dataArray = Array.isArray(data) ? data : [];
                setAnalyticsData(dataArray);
            } catch (err) {
                Logger.error("Error fetching experiment comparison data", err);
                setError("Failed to fetch experiment comparison data");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [customerConfig, filters]);

    if (isLoading) {
        return (
            <div className="text-center" style={{ padding: "40px" }}>
                <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ marginBottom: "40px" }}>
                <h4>Experiment Comparison</h4>
                <Alert variant="danger">{error}</Alert>
            </div>
        );
    }

    const chartData = transformDataForChart(analyticsData);
    
    // Calculate chart height based on number of data points
    const chartHeight = Math.min(500, Math.max(300, chartData.length * 35));

    return (
        <div style={{ marginBottom: "40px" }}>
            <h4>Experiment Comparison</h4>
            
            {analyticsData.length === 0 ? (
                <Alert variant="info">No data available for the selected date range.</Alert>
            ) : (
                <>
                    {/* Display mode toggle */}
                    <div style={{ marginBottom: "15px" }}>
                        <ButtonGroup size="sm">
                            <Button 
                                variant={displayMode === "absolute" ? "primary" : "outline-primary"}
                                onClick={() => setDisplayMode("absolute")}
                            >
                                Absolute Counts
                            </Button>
                            <Button 
                                variant={displayMode === "percentage" ? "primary" : "outline-primary"}
                                onClick={() => setDisplayMode("percentage")}
                            >
                                Conversion Rate
                            </Button>
                        </ButtonGroup>
                    </div>

                    <div style={{ 
                        backgroundColor: theme.chartBg, 
                        padding: "20px", 
                        borderRadius: "5px",
                        marginBottom: "20px"
                    }}>
                        <RESP width="100%" height={chartHeight}>
                            <CC 
                                data={chartData}
                                layout="vertical"
                                margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
                            >
                                <CG strokeDasharray="3 3" />
                                <XA type="number" />
                                <YA 
                                    type="category" 
                                    dataKey="label"
                                    width={140}
                                    tick={{ fontSize: 11 }}
                                />
                                <TT 
                                    formatter={(value: number, name: string) => {
                                        if (name === "Conversion Rate") return `${value}%`;
                                        return value.toLocaleString();
                                    }}
                                />
                                <LG />
                                
                                {displayMode === "absolute" ? (
                                    <>
                                        <BR 
                                            dataKey="invocation_count" 
                                            name="Invocations" 
                                            fill="#8884d8"
                                            barSize={15}
                                        />
                                        <BR 
                                            dataKey="play_count" 
                                            name="Plays" 
                                            fill="#82ca9d"
                                            barSize={15}
                                        />
                                    </>
                                ) : (
                                    <BR 
                                        dataKey="conversion_rate" 
                                        name="Conversion Rate" 
                                        fill="#ffc658"
                                        barSize={20}
                                    />
                                )}
                                
                                {displayMode === "absolute" && (
                                    <LN
                                        type="monotone"
                                        dataKey="conversion_rate"
                                        stroke="#ff7c7c"
                                        strokeWidth={2}
                                        name="Conversion Rate"
                                        dot={{ fill: "#ff7c7c", strokeWidth: 2 }}
                                    />
                                )}
                            </CC>
                        </RESP>
                    </div>

                    {/* Summary table */}
                    <div style={{ 
                        backgroundColor: theme.chartBg, 
                        padding: "15px", 
                        borderRadius: "5px",
                        marginBottom: "20px",
                        overflowX: "auto"
                    }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: "2px solid #ddd" }}>
                                    <th 
                                        style={{ padding: "10px", textAlign: "left", cursor: "pointer" }}
                                        onClick={() => handleSort("experiment_title")}
                                    >
                                        Experiment<SortIcon field="experiment_title" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "left", cursor: "pointer" }}
                                        onClick={() => handleSort("variant")}
                                    >
                                        Variant<SortIcon field="variant" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("invocation_count")}
                                    >
                                        Invocations<SortIcon field="invocation_count" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("play_count")}
                                    >
                                        Plays<SortIcon field="play_count" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("conversion_rate")}
                                    >
                                        Conversion Rate<SortIcon field="conversion_rate" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((row, idx) => (
                                    <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                                        <td style={{ padding: "10px" }}>{row.experiment_title}</td>
                                        <td style={{ padding: "10px" }}>{row.variant}</td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.invocation_count.toLocaleString()}
                                        </td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.play_count.toLocaleString()}
                                        </td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.conversion_rate}%
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <details>
                        <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "10px" }}>
                            View Raw Data
                        </summary>
                        <pre style={{ 
                            backgroundColor: "#f5f5f5", 
                            padding: "15px", 
                            borderRadius: "5px",
                            maxHeight: "400px",
                            overflow: "auto"
                        }}>
                            {JSON.stringify(analyticsData, null, 2)}
                        </pre>
                    </details>
                </>
            )}
        </div>
    );
};

export default ExperimentComparisonWidget;
